/**
 * Listing price synthesis — computes the displayable price info for a listing
 * from its base price, discount price + window, and per-variant prices.
 *
 * Ported from `PopshopRN/src/functions/listing/listingPriceUtils.ts`. Trimmed
 * to the pure computation: the React `useSynthesizedListingPriceInfo` hook and
 * its `anyListingItemSimplified` memo-shaping helper are dropped (React
 * Compiler memoizes the bare `getSynthesizedListingPriceInfo` call), and the
 * `listingPriceValidator` form validator stays out (validation is a separate
 * concern). Price formatting goes through `@ps/functions/utils/currency-formatter`.
 */
import {
  type CurrencyFormatterOptions,
  currencyFormatter,
} from './currency-formatter';

/** A Firestore `Timestamp`-like value, an ISO string, or a native `Date`. */
export type DateLike = Date | string | { toDate(): Date };

export type AnyItemWithPrice = {
  price: number;
  discountPrice?: number;
};

export type AnyItemWithPriceWithId = AnyItemWithPrice & { id: string };

export type AnyListingItem = AnyItemWithPrice & {
  discountEnabled?: boolean;
  discountStartDate?: DateLike;
  discountEndDate?: DateLike;
  variants?: Partial<AnyItemWithPrice>[];
};

export type AnyListingItemWithId = Omit<AnyListingItem, 'variants'> & {
  id: string;
  variants?: Partial<AnyItemWithPriceWithId>[];
};

export type ListingPriceRange = { min: number; max: number };

/** Sentinel for "no resolvable price". */
export const kInvalidListingPrice = -1;
/** A non-free listing must be priced at or above this floor. */
export const kMinNonFreeListingPrice = 0.5;

export type SynthesizedListingPriceInfo = {
  priceRange: ListingPriceRange | undefined;
  discountPriceRange?: ListingPriceRange;
  displayText: {
    price: string;
    originalPrice?: string;
  };
};

type PriceItems = {
  priceItems: number[];
  discountPriceItems: number[];
};

export const hasValidListingPrice = (info: SynthesizedListingPriceInfo) => {
  return info.discountPriceRange != null || info.priceRange != null;
};

export const toDate = (date: DateLike | null | undefined) => {
  try {
    if (date == null) return date;
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    return date.toDate();
  } catch (error) {
    console.error('Failed to convert date', date, error);
    return undefined;
  }
};

const isDiscountWithinWindow = (item: AnyListingItem) => {
  const discountStartDate = toDate(item.discountStartDate);
  const discountEndDate = toDate(item.discountEndDate);
  const now = Date.now();
  return (
    (discountStartDate == null || now >= discountStartDate.getTime()) &&
    (discountEndDate == null || now <= discountEndDate.getTime())
  );
};

const priceItemsFromListingPrice = (item: AnyListingItem) => {
  const result: PriceItems = { priceItems: [], discountPriceItems: [] };

  let discountPrice: number | undefined = undefined;

  if (item.discountEnabled !== false && item.discountPrice != null) {
    discountPrice = isDiscountWithinWindow(item)
      ? item.discountPrice
      : undefined;
  }

  if (item.price >= 0) {
    result.priceItems.push(item.price);
    if (
      discountPrice != null &&
      discountPrice >= 0 &&
      discountPrice <= item.price
    ) {
      result.discountPriceItems.push(discountPrice);
    } else {
      // make the discount price equal to listing price so that
      // we can determine the listing price range correctly.
      result.discountPriceItems.push(item.price);
    }
  }
  return result;
};

const priceItemsFromVariants = (item: AnyListingItem) => {
  const result: PriceItems = { priceItems: [], discountPriceItems: [] };

  if (item.variants != null) {
    for (const variant of item.variants) {
      if (variant.price != null && variant.price >= 0) {
        result.priceItems.push(variant.price);
        if (
          variant.discountPrice != null &&
          variant.discountPrice >= 0 &&
          variant.discountPrice <= variant.price
        ) {
          result.discountPriceItems.push(variant.discountPrice);
        } else {
          // make the discount price equal to variant price so that
          // we can determine the discount price range correctly.
          result.discountPriceItems.push(variant.price);
        }
      }
    }
  }

  return result;
};

export const getSynthesizedListingPrices = (
  item: AnyListingItem,
  options?: {
    prefer?: 'listing' | 'variant';
  },
): {
  priceRange: ListingPriceRange | undefined;
  discountPriceRange?: ListingPriceRange;
} => {
  let resultPriceItems: PriceItems;

  if (options?.prefer === 'listing') {
    resultPriceItems = priceItemsFromListingPrice(item);
    if (
      resultPriceItems.priceItems.length === 0 &&
      resultPriceItems.discountPriceItems.length === 0
    ) {
      resultPriceItems = priceItemsFromVariants(item);
    }
  } else {
    resultPriceItems = priceItemsFromVariants(item);
    if (
      resultPriceItems.priceItems.length === 0 &&
      resultPriceItems.discountPriceItems.length === 0
    ) {
      resultPriceItems = priceItemsFromListingPrice(item);
    }
  }

  const priceRange: ListingPriceRange | undefined =
    resultPriceItems.priceItems.length > 0
      ? {
          min: Math.min(...resultPriceItems.priceItems),
          max: Math.max(...resultPriceItems.priceItems),
        }
      : undefined;

  const discountPriceRange: ListingPriceRange | undefined =
    resultPriceItems.discountPriceItems.length > 0
      ? {
          min: Math.min(...resultPriceItems.discountPriceItems),
          max: Math.max(...resultPriceItems.discountPriceItems),
        }
      : undefined;

  return {
    priceRange,
    discountPriceRange:
      discountPriceRange?.min !== priceRange?.min ||
      discountPriceRange?.max !== priceRange?.max
        ? discountPriceRange
        : undefined,
  };
};

export type ListingPriceDisplayOptions = {
  prefer?: 'listing' | 'variant';
  displayValueAs?: 'min' | 'max' | 'range';
  formatPrice?: (
    value: number | undefined,
    options?: CurrencyFormatterOptions,
  ) => string;
  formatRangeText?: (lower: string, upper: string) => string;
  formatOptions?: CurrencyFormatterOptions;
};

const defaultRangeTextFormatter = (lower: string, upper: string) =>
  [lower, upper].join(' - ');

const defaultPriceTextFormatter = (
  value: number | undefined,
  options?: CurrencyFormatterOptions,
) => currencyFormatter.format(value, options);

export const getSynthesizedListingPriceInfo = (
  item: AnyListingItem,
  options?: ListingPriceDisplayOptions,
): SynthesizedListingPriceInfo => {
  const { priceRange, discountPriceRange } = getSynthesizedListingPrices(item, {
    prefer: options?.prefer,
  });

  const displayText: SynthesizedListingPriceInfo['displayText'] = { price: '' };

  const displayValueAs = options?.displayValueAs ?? 'min';
  const formatPrice = options?.formatPrice ?? defaultPriceTextFormatter;
  const formatOptions = options?.formatOptions;
  const applyDisplayTextWith = (
    selectValue: (range: ListingPriceRange) => number,
  ) => {
    if (discountPriceRange != null) {
      const discountPrice = selectValue(discountPriceRange);
      displayText.price = formatPrice(discountPrice, formatOptions);
      if (priceRange != null) {
        const price = selectValue(priceRange);
        if (discountPrice < price) {
          displayText.originalPrice = formatPrice(price, formatOptions);
        }
      }
    } else {
      displayText.price = formatPrice(
        priceRange == null ? undefined : selectValue(priceRange),
        formatOptions,
      );
    }
  };

  if (displayValueAs === 'range') {
    const formatPriceRange =
      options?.formatRangeText ?? defaultRangeTextFormatter;
    const textFromRange = (range: ListingPriceRange | undefined) => {
      if (range == null) return formatPrice(undefined, formatOptions);
      const [lower, upper] = [
        formatPrice(range.min, formatOptions),
        formatPrice(range.max, formatOptions),
      ];
      return lower === upper ? lower : formatPriceRange(lower, upper);
    };
    if (discountPriceRange != null) {
      displayText.price = textFromRange(discountPriceRange);
      if (priceRange != null) {
        if (
          priceRange.min !== discountPriceRange.min ||
          priceRange.max !== discountPriceRange.max
        ) {
          displayText.originalPrice = textFromRange(priceRange);
        }
      }
    } else {
      displayText.price = textFromRange(priceRange);
    }
  } else {
    applyDisplayTextWith(range =>
      displayValueAs === 'min' ? range.min : range.max,
    );
  }

  return {
    priceRange,
    discountPriceRange,
    displayText,
  };
};

export const isListingPriceFree = (priceInfo: SynthesizedListingPriceInfo) => {
  return (
    (priceInfo.discountPriceRange != null &&
      priceInfo.discountPriceRange.max === 0) ||
    (priceInfo.priceRange != null && priceInfo.priceRange.max === 0)
  );
};

export const getVariantCurrentPrice = (
  listing: AnyListingItemWithId,
  variantId: string | null | undefined,
) => {
  const currentVariant = listing.variants?.find(
    variant => variant.id === variantId,
  );
  const { priceRange, discountPriceRange } = getSynthesizedListingPrices({
    price: listing.price,
    discountPrice: listing.discountPrice,
    discountEnabled: listing.discountEnabled,
    discountStartDate: listing.discountStartDate,
    discountEndDate: listing.discountEndDate,
    variants: currentVariant ? [currentVariant] : [],
  });
  return discountPriceRange?.min ?? priceRange?.min;
};
